import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';

import { notificationService } from '@/lib/services/notification.service';

import {
  type CreatePersonaFormData,
  FORM_STEP_ARRAY,
  buildCreatePersonaSchema,
  defaultFormData,
  mapFormDataToCreatePayload,
} from '../../CreatePersonaForm.schema';
import { CreatePersonaFormConfig, UseCreatePersonaFormOptions } from './useCreatePersonaForm.types';

/**
 * Return type for the create persona form hook.
 */
export type UseCreatePersonaFormReturn = ReturnType<typeof useCreatePersonaForm>;

/**
 * Main hook for the create persona form.
 * Uses React Hook Form with Zod validation.
 *
 * The persona-create async caller is INJECTED via options
 * (`createPersona`) per Springboard dependency injection — keeps `features/audiences`
 * boundary-clean (no cross-feature OUT to `@/features/persona-sparks`).
 */
export function useCreatePersonaForm(options: UseCreatePersonaFormOptions) {
  const { createPersona, onSuccess, onViewPersona } = options;

  const { t } = useTranslation('audiences');
  const { t: tValidation } = useTranslation('validation');

  const schema = useMemo(() => buildCreatePersonaSchema(tValidation), [tValidation]);

  // React Hook Form
  const form = useForm<CreatePersonaFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultFormData,
    mode: 'onTouched',
  });

  // Step navigation state
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const activeStepId = FORM_STEP_ARRAY[activeStepIndex].id;
  const isFirstStep = activeStepIndex === 0;
  const isLastStep = activeStepIndex === FORM_STEP_ARRAY.length - 1;

  /**
   * Submit handler - validates all fields and submits.
   */
  const onSubmit = useCallback(
    async (formData: CreatePersonaFormData) => {
      const payload = mapFormDataToCreatePayload(formData);

      try {
        const response = await createPersona({
          payload,
        });
        const personaId = response.personaId;
        const isProcessing =
          response.status === 'processing' || (response.status === undefined && Boolean(personaId));

        notificationService.showSuccess(
          isProcessing
            ? t('audiences.useCreatePersonaForm.audienceCreationStarted')
            : t('audiences.useCreatePersonaForm.personaSuccessfullyCreated'),
          {
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'bottom',
            },
            action: onViewPersona
              ? {
                  label: isProcessing
                    ? t('audiences.useCreatePersonaForm.viewProgress')
                    : t('audiences.useCreatePersonaForm.viewPersona'),
                  onClick: () => onViewPersona(personaId),
                }
              : undefined,
          },
        );
        onSuccess?.();
      } catch {
        notificationService.showError(t('audiences.useCreatePersonaForm.personaFailedToCreate'), {
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'bottom',
          },
          description: t('audiences.useCreatePersonaForm.tryAgainAndIfTheError'),
        });
      }
    },
    [createPersona, onSuccess, onViewPersona, t],
  );

  /**
   * Go to next step or submit on last step.
   * Validates the current step's required fields before advancing.
   */
  const goNext = useCallback(async () => {
    const currentStep = FORM_STEP_ARRAY[activeStepIndex];
    const isValid = await form.trigger(currentStep.requiredFields);
    if (!isValid) return;

    if (isLastStep) {
      await form.handleSubmit(onSubmit)();
    } else {
      setActiveStepIndex((prev) => prev + 1);
    }
  }, [activeStepIndex, isLastStep, form, onSubmit]);

  /**
   * Go to previous step.
   */
  const goBack = useCallback(() => {
    if (!isFirstStep) {
      setActiveStepIndex((prev) => prev - 1);
    }
  }, [isFirstStep]);

  const canGoBack = !isFirstStep && !form.formState.isSubmitting;
  const canContinue = !form.formState.isSubmitting;

  return {
    ...form,
    activeStepId,
    activeStepIndex,
    isFirstStep,
    isLastStep,
    canContinue,
    canGoBack,
    goNext,
    goBack,
    config: {
      segmentNameOptions: options.config?.segmentNameOptions ?? [],
      segmentDescriptionByName: options.config?.segmentDescriptionByName ?? {},
      topicsOfInterestOptions: options.config?.topicsOfInterestOptions ?? [],
    } satisfies CreatePersonaFormConfig,
  };
}
